import { useQuery } from '@tanstack/react-query';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { CinemaProductDto } from '@cineamo/api/models/cinema-product/CinemaProductDto.types';
import { CinemaProductCategoryDto } from '@cineamo/api/models/cinema-product-category/CinemaProductCategoryDto.types';

import { getCinemaProductsByCinemaId } from '@cineamo/api/resources/cinema-product/cinema-product-api';

import { getAllListItems } from '@cineamo/ui/src/helpers/get-all-list-items';

export const CONCESSION_CATALOG_QUERY_KEY = 'concession-catalog';

export type ConcessionTab = {
    /** `null` is the fallback tab for products without a category. */
    categoryId: CinemaProductCategoryDto['id'] | null;
    name: string | null;
    position: number;
    products: CinemaProductDto[];
};

/**
 * The products a cinema sells online, grouped into one tab per category from the products' inline `categories`
 * (one list call). A product in several categories shows up in each; products in none land in a trailing
 * fallback tab.
 */
export function useConcessionCatalog(cinemaId: CinemaDto['id'], enabled: boolean) {
    const { data: tabs = [], isLoading } = useQuery({
        queryKey: [CONCESSION_CATALOG_QUERY_KEY, `cinemaId-${cinemaId}`],
        queryFn: async (): Promise<ConcessionTab[]> => {
            const products = await getAllListItems({
                queryFn: (page: number) => getCinemaProductsByCinemaId({ cinemaId, sellableOnline: true, page }),
                listAccessor: 'cinema-products'
            });

            const byCategory = new Map<CinemaProductCategoryDto['id'], ConcessionTab>();
            const uncategorized: CinemaProductDto[] = [];
            for (const product of products) {
                if (!product.categories?.length) {
                    uncategorized.push(product);
                    continue;
                }
                for (const { id, name, position } of product.categories) {
                    const tab = byCategory.get(id) ?? { categoryId: id, name, position: position ?? 0, products: [] };
                    tab.products.push(product);
                    byCategory.set(id, tab);
                }
            }

            const categorized = [...byCategory.values()].sort(
                (a, b) => a.position - b.position || (a.name ?? '').localeCompare(b.name ?? '')
            );
            return uncategorized.length > 0
                ? [...categorized, { categoryId: null, name: null, position: 0, products: uncategorized }]
                : categorized;
        },
        staleTime: 5 * 60 * 1000,
        enabled
    });

    return { tabs, isLoading };
}
